import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getUserProfile, getCurrentStreak, getMonthlyStreakData, getActivityHeatData } from '../utils/database';
import DashboardSidebar from '../components/DashboardSidebar';
import BottomNav from '../components/BottomNav';
import AwardIcon from '../assets/icons/award.svg';
import '../styles/Dashboard.css';
import '../styles/Awards.css';

function Awards() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [, setLoading] = useState(true);
  
  // Streak data state
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [activityHeatData, setActivityHeatData] = useState([]);
  const [streaksLoaded, setStreaksLoaded] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (!currentUser || currentUser.uid !== userId) {
          navigate('/signin');
          return;
        }

        const userData = await getUserProfile(userId);
        if (!userData) {
          navigate('/onboarding');
          return;
        }

        setUser(userData);
        
        // Load streak data
        const now = new Date();
        const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const endDate = new Date(now);
        const startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 89); // 90 days for Awards page (3 months)
        const startDateStr = formatDate(startDate);
        const endDateStr = formatDate(endDate);
        
        // Get current streak
        const streak = await getCurrentStreak(userId);
        setCurrentStreak(streak);
        
        // Get streak history data to calculate longest streak
        const streakData = await getMonthlyStreakData(userId, now.getFullYear(), now.getMonth(), startDateStr, endDateStr);
        if (streakData && streakData.length > 0) {
          const maxStreak = Math.max(...streakData.map(d => d.value || 0), streak);
          setLongestStreak(maxStreak);
        } else {
          setLongestStreak(streak);
        }
        
        // Get activity heat data for visualization (90 days for Awards page)
        const heatData = await getActivityHeatData(userId, 90);
        setActivityHeatData(heatData);
        
        setStreaksLoaded(true);
        setLoading(false);
      } catch (error) {
        console.error('[Awards] Error loading user:', error);
        navigate('/signin');
      }
    });

    return () => unsubscribe();
  }, [userId, navigate]);

  // Calculate active days count
  const activeDaysCount = activityHeatData.filter(d => d.hasActivity).length;

  return (
    <>
    <div className="dashboard-page has-bottom-nav">
      {/* Background effects */}
      <div className="dashboard-bg-glow dashboard-bg-glow-1" />
      <div className="dashboard-bg-glow dashboard-bg-glow-2" />
      <div className="dashboard-overlay" />

      {/* Sidebar */}
      <DashboardSidebar userId={userId} user={user} />

      {/* Main content */}
      <div className="dashboard-main">
        {/* Header */}
        <div className="dashboard-header">
          <div className="dashboard-greeting">
            <h1>Awards</h1>
            <p className="dashboard-quote">Track your consistency and celebrate your streaks</p>
          </div>
        </div>

        {/* Streaks Section - Full Width Card */}
        <div className="awards-section">
          <div className="dashboard-card awards-streaks-card">
            <div className="dashboard-card-header">
              <h2>Streaks</h2>
              <p className="dashboard-activity-count">Last 90 days</p>
            </div>

            {/* Skeleton loading state */}
            {!streaksLoaded && (
              <div className="awards-streak-skeleton">
                <div className="dashboard-skeleton" style={{ height: '48px', width: '100%', marginBottom: '24px' }} />
                <div className="awards-streak-stats-skeleton">
                  <div className="dashboard-skeleton" style={{ height: '120px', flex: 1 }} />
                  <div className="dashboard-skeleton" style={{ height: '120px', flex: 1 }} />
                  <div className="dashboard-skeleton" style={{ height: '120px', flex: 1 }} />
                </div>
              </div>
            )}

            {/* Loaded content */}
            {streaksLoaded && (
              <>
                {/* Heat Strip - Full Width Activity Timeline */}
                <div className="awards-heat-strip-container">
                  <div className="awards-heat-strip">
                    {activityHeatData.map((day, index) => (
                      <div 
                        key={day.date}
                        className={`awards-heat-day ${day.hasActivity ? 'active' : ''}`}
                        title={day.hasActivity && day.activities?.length > 0 
                          ? `${day.date}: ${day.activities.join(', ')}`
                          : `${day.date}: No activity`}
                      />
                    ))}
                  </div>
                  <div className="awards-heat-labels">
                    <span>90 days ago</span>
                    <span>Today</span>
                  </div>
                </div>

                {/* Streak Stats */}
                <div className="awards-streak-stats">
                  {/* Current Streak */}
                  <div className="awards-streak-stat">
                    <div className="awards-streak-stat-value">{currentStreak}</div>
                    <div className="awards-streak-stat-label">Current Streak</div>
                    <div className="awards-streak-stat-sublabel">consecutive days</div>
                  </div>

                  {/* Longest Streak */}
                  <div className="awards-streak-stat">
                    <div className="awards-streak-stat-value">{longestStreak}</div>
                    <div className="awards-streak-stat-label">Longest Streak</div>
                    <div className="awards-streak-stat-sublabel">personal best</div>
                  </div>

                  {/* Active Days */}
                  <div className="awards-streak-stat">
                    <div className="awards-streak-stat-value">{activeDaysCount}</div>
                    <div className="awards-streak-stat-label">Active Days</div>
                    <div className="awards-streak-stat-sublabel">last 90 days</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Future Sections Placeholder */}
        <div className="awards-section awards-section-muted">
          <div className="awards-section-header">
            <h2>Achievements</h2>
            <span className="awards-coming-soon">Coming Soon</span>
          </div>
          <div className="awards-placeholder">
            <div className="awards-placeholder-icon">
              <img src={AwardIcon} alt="Achievements" />
            </div>
            <p>Badges and achievements will appear here as you reach new milestones</p>
          </div>
        </div>
      </div>
    </div>

    {/* Bottom Navigation - Mobile/Tablet */}
    <BottomNav userId={userId} user={user} />
    </>
  );
}

export default Awards;
